import { useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useSearchParams } from 'react-router'

import { useTRPC } from '@langmail/shared-ui'

import { useAccounts } from '@/features/account/hooks/use-accounts'
import { useToast } from '@/hooks/ui/use-toast'

import { AccountRow } from './account-row'
import { AddGmailButton } from './add-gmail-button'

/**
 * Settings surface for managing all Gmail accounts attached to the user.
 * Driven by `useAccounts()` which sources from `trpc.accounts.list`.
 */
export function AccountsList() {
  const { accounts, primaryAccountId, isLoading } = useAccounts()
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [searchParams, setSearchParams] = useSearchParams()

  // Surface flash messages set by `auth-callback.tsx` (link/reconnect flow)
  // and the email-mismatch guard, then strip them from the URL so a refresh
  // doesn't re-show the toast.
  useEffect(() => {
    const connected = searchParams.get('connected')
    const errorParam = searchParams.get('error')
    let consumed = false

    if (connected) {
      // The address isn't known at the callback anymore (the server hook
      // provisions the account), so the toast is static rather than per-email.
      toast({ title: 'Gmail connected' })
      queryClient.invalidateQueries({ queryKey: trpc.accounts.list.queryKey() })
      consumed = true
    }
    if (errorParam === 'email_mismatch_on_reconnect') {
      toast({
        title: 'Reconnect aborted',
        description: 'The Google account you signed in with does not match the primary account.',
        variant: 'destructive',
      })
      consumed = true
    }
    if (errorParam === 'required_scopes_missing') {
      toast({
        title: 'Missing Gmail permissions',
        description: 'Please grant Gmail access to connect this account.',
        variant: 'destructive',
      })
      consumed = true
    }

    if (consumed) {
      const next = new URLSearchParams(searchParams)
      next.delete('connected')
      next.delete('mode')
      next.delete('error')
      setSearchParams(next, { replace: true })
    }
  }, [searchParams, setSearchParams, toast, queryClient, trpc])

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="font-medium font-mono text-[11px] text-muted-foreground uppercase tracking-[1.5px]">
          Connected Gmail accounts
        </div>
        <AddGmailButton />
      </div>
      {isLoading ? (
        <div className="space-y-2">
          {[0, 1].map((idx) => (
            <div className="h-10 animate-pulse rounded-lg bg-muted" key={idx} />
          ))}
        </div>
      ) : null}
      {!isLoading && accounts.length === 0 && (
        <div className="text-[14px] text-muted-foreground">No Gmail accounts connected yet.</div>
      )}
      {!isLoading && accounts.length > 0 && (
        <div className="space-y-2">
          {accounts.map((account) => (
            <AccountRow account={account} isPrimary={account.id === primaryAccountId} key={account.id} />
          ))}
        </div>
      )}
    </div>
  )
}
